// Componentes reutilizáveis: funções puras que retornam TemplateResult (§8.2).
// Sem classes, sem estado interno. Eventos via sintaxe do lit-html (@click).
import { html, type TemplateResult } from 'lit-html';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { gravatarUrl } from '../lib/gravatar.js';

export type IconName =
  | 'plus' | 'search' | 'trash' | 'share' | 'download' | 'users' | 'logout'
  | 'sun' | 'moon' | 'check' | 'x' | 'brain' | 'play' | 'restore' | 'dots'
  | 'chevron-left' | 'chevron-right' | 'copy' | 'edit' | 'folder' | 'folder-open' | 'admin'
  | 'zoom-in' | 'zoom-out' | 'expand' | 'link' | 'note' | 'tag' | 'image' | 'paperclip' | 'palette'
  | 'grid' | 'list' | 'sort' | 'add-child' | 'add-sibling' | 'more' | 'comment' | 'columns' | 'sparkles' | 'checklist' | 'plug' | 'copy-doc';

// Ícones via Font Awesome Pro (classes fa-*). Carregado no <head> (padrão da casa).
const FA: Record<IconName, string> = {
  plus: 'fa-plus', search: 'fa-magnifying-glass', trash: 'fa-trash', share: 'fa-share-nodes',
  download: 'fa-download', users: 'fa-users', logout: 'fa-right-from-bracket',
  sun: 'fa-sun', moon: 'fa-moon', check: 'fa-check', x: 'fa-xmark',
  brain: 'fa-brain', play: 'fa-play', restore: 'fa-rotate-left', dots: 'fa-ellipsis-vertical',
  'chevron-left': 'fa-chevron-left', 'chevron-right': 'fa-chevron-right',
  copy: 'fa-copy', edit: 'fa-pen', folder: 'fa-folder', 'folder-open': 'fa-folder-open', admin: 'fa-user-gear',
  'zoom-in': 'fa-magnifying-glass-plus', 'zoom-out': 'fa-magnifying-glass-minus',
  expand: 'fa-expand', link: 'fa-link', note: 'fa-note-sticky', tag: 'fa-tag',
  image: 'fa-image', paperclip: 'fa-paperclip', palette: 'fa-palette',
  grid: 'fa-grid-2', list: 'fa-list', sort: 'fa-arrow-down-wide-short',
  'add-child': 'fa-diagram-subtask', 'add-sibling': 'fa-diagram-next',
  more: 'fa-ellipsis', comment: 'fa-comment', columns: 'fa-table-columns',
  sparkles: 'fa-wand-magic-sparkles', checklist: 'fa-list-check', plug: 'fa-plug', 'copy-doc': 'fa-clipboard',
};

export function icon(name: IconName, extra = ''): TemplateResult {
  return html`<i class="fa-regular ${FA[name]} ${extra}" aria-hidden="true"></i>`;
}

interface ButtonOpts {
  label?: string;
  icon?: IconName;
  variant?: 'primary' | 'ghost' | 'danger' | 'subtle';
  type?: 'button' | 'submit';
  disabled?: boolean;
  ariaLabel?: string;
  onClick?: (e: Event) => void;
}

export function button(opts: ButtonOpts): TemplateResult {
  const variant = opts.variant ?? 'primary';
  return html`
    <button
      class="btn btn-${variant}"
      type=${opts.type ?? 'button'}
      ?disabled=${opts.disabled ?? false}
      aria-label=${ifDefined(opts.ariaLabel ?? (opts.label ? undefined : opts.icon))}
      @click=${opts.onClick}
    >
      ${opts.icon ? icon(opts.icon) : ''}${opts.label ? html`<span>${opts.label}</span>` : ''}
    </button>
  `;
}

export function spinner(label = 'Carregando…'): TemplateResult {
  return html`<div class="spinner" role="status"><span class="spinner-dot"></span>${label}</div>`;
}

export function emptyState(opts: { icon: IconName; title: string; hint?: string; action?: TemplateResult }): TemplateResult {
  return html`
    <div class="empty">
      <div class="empty-icon">${icon(opts.icon)}</div>
      <h3>${opts.title}</h3>
      ${opts.hint ? html`<p>${opts.hint}</p>` : ''}
      ${opts.action ?? ''}
    </div>
  `;
}

export function avatar(name: string, initialsText: string, color: string): TemplateResult {
  return html`<span class="avatar" style="--avatar-bg:${color}" title=${name}>${initialsText}</span>`;
}

/**
 * Avatar do usuário: iniciais sobre a cor + Gravatar por cima (removido no erro
 * quando não há Gravatar — d=404). size em px.
 */
export function userAvatar(opts: {
  name: string;
  email: string;
  color: string;
  initials: string;
  size?: number;
}): TemplateResult {
  const size = opts.size ?? 32;
  const url = gravatarUrl(opts.email, size);
  return html`
    <span
      class="avatar"
      style="--avatar-bg:${opts.color};width:${size}px;height:${size}px;font-size:${Math.round(size * 0.4)}px;position:relative;overflow:hidden"
      title=${opts.name}
    >
      ${opts.initials}
      <img
        src=${url}
        alt=""
        loading="lazy"
        style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border-radius:inherit"
        @error=${(e: Event) => (e.target as HTMLElement).remove()}
      />
    </span>
  `;
}
